/** Presentation helpers. Money is cents everywhere until it reaches a screen. */

export function money(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function longDate(value: string, timezone?: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(timezone && { timeZone: timezone }),
  }).format(new Date(dateSafe(value)));
}

export function shortDate(value: string, timezone?: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(timezone && { timeZone: timezone }),
  }).format(new Date(dateSafe(value)));
}

export function dateTime(value: string, timezone?: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timezone && { timeZone: timezone }),
  }).format(new Date(value));
}

/** A bare 'YYYY-MM-DD' parses as UTC midnight, which shifts a day westward. */
function dateSafe(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value;
}

export function minutesToTime(minutes: number): string {
  const hour = Math.floor(minutes / 60) % 24;
  const minute = minutes % 60;
  const suffix = hour >= 12 ? 'pm' : 'am';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return minute === 0 ? `${display}${suffix}` : `${display}:${String(minute).padStart(2, '0')}${suffix}`;
}

/** Rate wording that reads naturally under a price. */
export function rateSuffix(rateType: string): string {
  switch (rateType) {
    case 'per_night':
      return 'a night';
    case 'per_day':
      return 'a day';
    case 'per_visit':
      return 'a visit';
    case 'per_hour':
      return 'an hour';
    default:
      return '';
  }
}

export function titleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** 'Sep 1 to Sep 4', collapsing to one date when the range is a single day. */
export function dateRange(start: string, end: string, timezone?: string): string {
  const a = shortDate(start, timezone);
  const b = shortDate(end, timezone);
  return a === b ? a : `${a} to ${b}`;
}

export function relativeDays(dateOnly: string): string {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const target = new Date(`${dateOnly}T12:00:00`);
  const days = Math.round((target.getTime() - today.getTime()) / 86400000);

  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 0) return `in ${days} days`;
  return `${Math.abs(days)} days ago`;
}
